<template>
    <div class="w-full flex flex-col p-8 text-left">
        <div class="container mx-auto">
            <div v-if="hasPermission('update-mou-effectiveness-card') && mouEffectivenessCard"
                class="flex justify-end gap-4">
                <button @click="onAddNew" v-if="!isEditing && from == 'current-partners-index'" title="Add New"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg shadow-sm hover:bg-green-100 hover:text-green-800 transition duration-200">
                    <font-awesome-icon :icon="['fas', 'plus']" class="mr-2 text-green-600" />
                    Add New
                </button>
                <button
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg shadow-sm hover:bg-blue-100 hover:text-blue-800 transition duration-200"
                    @click="onEdit" v-if="!isEditing" title="Edit">
                    <font-awesome-icon :icon="['fas', 'pen']" class="mr-2 text-blue-600" />
                    Edit
                </button>
            </div>
            <mou-effectiveness-card-view :class="[hasPermission('update-mou-effectiveness-card') ? 'mt-6' : 'mt-0']"
                v-if="!isEditing && mouEffectivenessCard"
                :mou-effectiveness-card="mouEffectivenessCard"></mou-effectiveness-card-view>

            <mou-effectiveness-card-edit v-else-if="isEditing" :mou-effectiveness-card="mouEffectivenessCard"
                :mode="mode" @cancel="cancelEditView" @saved="onSave" />

            <div class="h-full flex justify-center items-center" v-if="isLoading">
                <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
            </div>
        </div>
    </div>
</template>

<script>
import gql from "graphql-tag";
import MouEffectivenessCardView from "./view.vue";
import MouEffectivenessCardEdit from "./edit.vue";

const UPDATE_MOU_EFFECTIVENESS_CARD_MUTATION = gql`
  mutation UpdateMouEffectivenessCard($current_partner_id: ID!, $id: ID, $input: MouEffectivenessCardInput!, $mode: String!) {
    updateMouEffectivenessCard(current_partner_id: $current_partner_id, id: $id, input: $input, mode: $mode) {
      error
      error_code
      message
    }
  }
`;

export default {
    name: "mou-effectiveness-card-index",
    components: {
        MouEffectivenessCardView,
        MouEffectivenessCardEdit,
    },
    props: ["currentPartnerId", "from", "mouId"],
    data() {
        return {
            isLoading: false,
            isEditing: false,
            mouEffectivenessCard: null,
            mode: 'update',
        };
    },
    async created() {
        await this.fetchDetails();
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
    },
    methods: {
        hasPermission(crudName) {
            if (this.role === 'superadmin') return true;
            const permissions = this.$store.getters.permissions
                ? this.$store.getters.permissions.split(',')
                : [];
            return permissions.includes(crudName);
        },
        async fetchDetails() {
            this.isLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetMouEffectivenessCard(
                            $current_partner_id: ID
                            $mou_id: ID
                            $from: String
                        ) {
                        getMouEffectivenessCard(
                            current_partner_id: $current_partner_id
                            mou_id: $mou_id
                            from: $from
                        ) {
                            error
                            error_code
                            message
                            data {
                                id
                                first_party_labels
                                second_party
                                concerned_department
                                mou_signing_date
                                mou_end_date
                                mou_clauses
                                coordinator_first_party
                                coordinator_second_party
                                mouJointCommitteeMeetings {
                                    id
                                    year
                                    q1
                                    q2
                                    q3
                                    q4
                                }
                                moUOutcomes {
                                    name
                                    actual
                                    expected
                                }
                            }
                        }
                    }`,
                    variables: {
                        current_partner_id: parseInt(this.currentPartnerId),
                        mou_id: parseInt(this.mouId),
                        from: this.from,
                    },
                    fetchPolicy: "network-only",
                });
                const response = data.getMouEffectivenessCard;
                if (response.error) {
                    if (response.error_code == 403) {
                        this.$router.replace("/no-permission");
                    } else if (response.error_code == 401) {
                        this.$router.replace("/");
                    } else {
                        alert(response.message);
                        this.$emit('close');
                    }
                } else {
                    this.mouEffectivenessCard = response.data;
                }
            } catch (error) {
                console.log(error);
            }
            this.isLoading = false;
        },
        async onSave(updatedData) {
            this.isLoading = true;

            const payload = Object.fromEntries(
                Object.entries(updatedData).map(([key, value]) => [key, value.val])
            );

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: UPDATE_MOU_EFFECTIVENESS_CARD_MUTATION,
                    variables: {
                        current_partner_id: parseInt(this.currentPartnerId),
                        id: this.mode == 'update' ? parseInt(this.mouEffectivenessCard.id) : null,
                        input: payload,
                        mode: this.mode
                    },
                });

                const response = data.updateMouEffectivenessCard;

                if (response.error) {
                    return this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: response.message,
                    });
                }

                await this.$swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: response.message,
                    showConfirmButton: false,
                    timer: 1000,
                    timerProgressBar: true,
                });

                this.isEditing = false;
                await this.fetchDetails();

            } catch (error) {
                await this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: error.message || 'Could not update MoU Effectiveness Card.',
                });
            } finally {
                this.isLoading = false;
            }
        },
        onAddNew() {
            this.mode = 'create';
            this.isEditing = true;
        },
        onEdit() {
            this.mode = 'update';
            this.isEditing = true;
        },
        cancelEditView() {
            this.mode = 'update';
            this.isEditing = false;
        },
    },
}
</script>

<style lang="scss" scoped></style>